import { FC, Fragment, useEffect } from 'react';
// i18next
import { useTranslation } from 'react-i18next';
// Hooks
import useMenu from 'hooks/useMenu';
import useToggle from 'hooks/useToggle';
// Redux
import { useAppDispatch, useAppSelector } from 'hooks/useStore';
import { getMedia, saveGeneratedMedia } from 'store/media/mediaAsync';
import { mediaActions } from 'store/media/mediaSlice';
import { selectCkeckedMediaIds, selectMedia } from 'store/media/mediaSelectors';
import { selectAIModuleFeatureEnable, selectCurrentUser } from 'store/currentUser/currentUserSelectors';
import MediaFormats from 'types/MediaFormats';
import UserRoles from 'types/UserRoles';
// Components
import { Toolbar } from 'components/Toolbar';
import { Button } from 'components/Buttons';
import MediaList from './MediaList';
import UploadImageDialog from 'dialogs/UploadImage.dialog';
import UploadVideoDialog from 'dialogs/UploadVideo.dialog';
import CloneMediaDialog from 'dialogs/CloneMedia.dialog';
import UploadAudioDialog from 'dialogs/UploadAudio.dialog';
import GenerateImageDialog from 'dialogs/GenerateImage.dialog';
// Icons
import { FaMagic } from 'react-icons/fa';

const MediaPage:FC = () => {
  const { t } = useTranslation();

  const dispatch = useAppDispatch();

  const currentUser = useAppSelector(selectCurrentUser);
  const media = useAppSelector(selectMedia);
  const checkedMediaIds = useAppSelector(selectCkeckedMediaIds);
  const aiModuleEnabled = useAppSelector(selectAIModuleFeatureEnable);

  const { Menu, MenuItem, openMenu } = useMenu();
  const { Menu:MediaMenu, MenuItem:MediaMenuItem, openMenu:openMediaMenu } = useMenu();
  const { open:openImageDialog, toggle:toggleImageDialog } = useToggle();
  const { open:openVideoDialog, toggle:toggleVideoDialog } = useToggle();
  const { open:openAudioDialog, toggle:toggleAudioDialog } = useToggle();
  const { open:openCloneMediaDialog, toggle:toggleCloneMediaDialog } = useToggle();
  const { open:openGenerateDialog, toggle:toggleGenerateDialog } = useToggle();

  const handleApplyGeneratedImage = (url:string, format:MediaFormats) => {
    dispatch(saveGeneratedMedia({ url, format }));
  }

  const selectAll = () => dispatch(mediaActions.setCheckedMedia(media.map(m => m._id)));
  const unselectAll = () => dispatch(mediaActions.setCheckedMedia([]));

  useEffect(() => {
    dispatch(getMedia({ scope: ['account'] }));

    return () => {
      dispatch(mediaActions.setInitialField('media'));
    }
    // eslint-disable-next-line
  }, []);

  const isAdmin = currentUser?.groups.includes(UserRoles.Admin);

  return (
    <Fragment>
      {openImageDialog ? (
        <UploadImageDialog open={openImageDialog} onClose={toggleImageDialog} initialFormat={MediaFormats.Logo} />
      ) : null}
      {openVideoDialog ? (
        <UploadVideoDialog open={openVideoDialog} onClose={toggleVideoDialog} />
      ) : null}
      {openAudioDialog ? (
        <UploadAudioDialog open={openAudioDialog} onClose={toggleAudioDialog} />
      ) : null}
      {aiModuleEnabled && openGenerateDialog ? (
        <GenerateImageDialog
          open={openGenerateDialog}
          onClose={toggleGenerateDialog}
          format={MediaFormats.Logo}
          allowFormatChange
          onApply={handleApplyGeneratedImage}
        />
      ) : null}
      {openCloneMediaDialog && isAdmin ? (
        <CloneMediaDialog open={openCloneMediaDialog} onClose={toggleCloneMediaDialog} />
      ) : null}

      <div className="container max-w-screen-desktop flex-grow flex flex-col gap-4 py-4 overflow-y-auto">
        <Toolbar title={t('pages.media.title')} className="gap-2">
          {isAdmin ? (
            <Fragment>
              <Button
                variant="contained"
                type="button"
                onClick={openMediaMenu}
              >{t('buttons.actions')}</Button>
              <MediaMenu>
                <MediaMenuItem onClick={selectAll} disabled={media.length === checkedMediaIds.length}>{t('buttons.selectAll')}</MediaMenuItem>
                <MediaMenuItem onClick={unselectAll} disabled={!checkedMediaIds.length}>{t('buttons.deselectAll')}</MediaMenuItem>
                <MediaMenuItem onClick={toggleCloneMediaDialog} disabled={!checkedMediaIds.length}>{t('pages.media.cloneMediaButton')}</MediaMenuItem>
              </MediaMenu>
            </Fragment>
          ) : null}
          {aiModuleEnabled ? (
            <Button
              variant="outlined"
              type="button"
              onClick={toggleGenerateDialog}
            ><FaMagic className="inline-block mr-2 -mt-0.5" />{t('buttons.generateWithAi')}</Button>
          ) : null}
          <Button
            variant="contained"
            type="button"
            onClick={openMenu}
          >{t('pages.media.uploadMediaButton')}</Button>
          <Menu>
            <MenuItem onClick={toggleImageDialog}>{t('labels.image')}</MenuItem>
            <MenuItem onClick={toggleVideoDialog}>{t('labels.video')}</MenuItem>
            <MenuItem onClick={toggleAudioDialog}>{t('labels.audio')}</MenuItem>
          </Menu>
        </Toolbar>
        <MediaList />
      </div>
    </Fragment>
  )
}

export default MediaPage;
